import { TFunction } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StockFilter } from '../components/table/filters';
import StockCodeCell from '../components/table/StockCodeCell';
import { useFavStocks } from '../hooks/useFavoriteStocks';
import useLookupPageStockRegion from '../hooks/useLookupPageStockRegion';
import TutorialQuestionMark from '../modules/Help/TutorialQuestionMark';
import { FavStock } from '../py-generated/datalouder/favorite_stock/types';
import { Region } from '../shared/types';
import { ReactTable } from '../types';

const cache = {};
function quickRegex(exp) {
  if (cache[exp]) return cache[exp];
  cache[exp] = new RegExp(exp, 'i');
  return cache[exp];
}

export interface HKStockItem {
  stockCode: string;
  name_en: string;
  name_zh: string;
  region: Region;
  unlike: boolean;
}

const hkStockCodeAccessor = (item): HKStockItem => {
  return {
    stockCode: item.stockCode,
    name_en: item.name_en,
    name_zh: item.name_zh,
    region: 'hk',
    unlike: item.unlike,
  };
};

export interface USStockItem {
  stockCode: string;
  name_en: string;
  name_zh: string;
  region: Region;
  unlike: boolean;
}

const usStockCodeAccessor = (item): USStockItem => ({
  stockCode: item.stockCode,
  name_en: item.name_en,
  name_zh: item.name_zh || item.name_en,
  region: 'us',
  unlike: item.unlike,
});

const makeStockCodeFilterType =
  (stocks: FavStock[]) => (rows, id, filterValue) => {
    if (filterValue === '❤️')
      return rows.filter(
        row =>
          stocks === undefined ||
          stocks.map(s => s.ticker).includes(row.values[id].stockCode)
      );
    if (filterValue.startsWith('K#')) {
      return rows.filter(
        row =>
          row.values[id] &&
          quickRegex(filterValue.slice(2)).test(row.values[id].stockCode)
      );
    }
    return rows;
  };

const useStockCodeFilterType = () => {
  const { stocks } = useFavStocks();
  return useMemo(() => makeStockCodeFilterType(stocks), [stocks]);
};

export const useStockColumnProps = (
  stockRegion?: Region
): ReactTable.Column => {
  const { t } = useTranslation();
  const region = useLookupPageStockRegion(stockRegion);
  const stockCodeFilterType = useStockCodeFilterType();
  return useMemo(
    () => ({
      Header: (
        <span>
          {t('Stock Code')} <TutorialQuestionMark tutorialId={19} />
        </span>
      ),
      id: `${region}-stock-code`,
      className: 'stock-code-cell',
      width: region === 'us' ? 300 : 240,
      accessor: region === 'us' ? usStockCodeAccessor : hkStockCodeAccessor,
      exportAccessor: 'stockCode',
      Filter: StockFilter,
      filter: stockCodeFilterType,
      Cell: StockCodeCell,
      sortType: ({ original: a }, { original: b }) => {
        return a.stockCode.localeCompare(b.stockCode);
      },
      contentAlign: 'left',
    }),
    [region, t, stockCodeFilterType]
  );
};

const commonProperties = (t: TFunction) => {
  return {
    previousText: t('rt-previous-page'),
    nextText: t('rt-next-page'),
    loadingText: t('rt-loadingText'),
    noDataText: t('rt-noDataText'),
    pageText: t('rt-pageText'),
    ofText: t('rt-ofText'),
    rowsText: t('rt-rowsText'),
  };
};

const sortPriortizeNonZero: ReactTable.SortFunction = (
  rowA,
  rowB,
  columnId,
  desc
) => {
  const a = rowA.values[columnId],
    b = rowB.values[columnId];

  if (a === 0) return desc ? -1 : 1;
  if (b === 0) return desc ? 1 : -1;
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
};

const sortAbsoluteValue: ReactTable.SortFunction = (rowA, rowB, columnId) => {
  const a = Math.abs(rowA.values[columnId]),
    b = Math.abs(rowB.values[columnId]);
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
};

export default {
  commonProperties,
  sortPriortizeNonZero,
  sortAbsoluteValue,
};
